import StraataLogo from '@/components/Work/logos/StraataLogo';
import heroOverview from '@/assets/cases/straata/0.mov';
import heroOverviewBlur from '@/assets/cases/straata/0.blur.webp';

import {CaseStudy} from './types';
import landingImage from '@/assets/cases/straata/1.jpg';
import landingFeatures from '@/assets/cases/straata/2a.mov';
import landingCta from '@/assets/cases/straata/2b.jpg';
import landingFull from '@/assets/cases/straata/3.jpg';
import merchantAggregatesVideo from '@/assets/cases/straata/4.mov';
import transactionReconVideo from '@/assets/cases/straata/5.mov';
import savedViewsVideo from '@/assets/cases/straata/6a.mov';
import savedMetricsVideo from '@/assets/cases/straata/6b.mov';
import connectionCheckerImage from '@/assets/cases/straata/7.jpg';
import benchmarkConfigurationImage from '@/assets/cases/straata/7b.jpg';

const straataMvp: CaseStudy = {
    // ============================================
    // FIXED METADATA
    // ============================================
    id: 'straata-mvp',
    slug: 'straata-payment-analytics-platform',
    title: 'Straata',
    client: 'Utopaya',
    industry: 'FinTech / Payment Processing',
    projectType: 'Zero-to-One SaaS Platform Development',
    timeline: '5 weeks',
    technologies: [
        'React',
        'Next.js',
        'TypeScript',
        'Supabase',
        'PostgreSQL',
        'Stripe API',
        'AG Grid',
        'OAuth'
    ],

    shortDescription: 'Two humans, five weeks: Straata launched with an investor-ready landing page plus a production SaaS that turns opaque payment statements into instant benchmarks.',
    excerpt: 'We codified Utopaya\'s payments expertise into Straata—a multi-tenant analytics platform, ingestion engine, and benchmark studio—while shipping a public landing experience that captured beta demand and unlocked the seed narrative in under five weeks.',
    heroImageUrl: '/images/case-studies/straata/hero.jpg',
    logo: StraataLogo,
    featured: true,
    workHighlight: {
        id: 'straata',
        mediaType: 'video',
        media: heroOverview,
        mediaSources: [{src: heroOverview, type: 'video/quicktime'}],
        placeholder: heroOverviewBlur
    },

    // ============================================
    // HERO SECTION DATA
    // ============================================
    heroStatement:
        'In five weeks we created a production ready digital twin of payment processing consulting - taking millions of transactions and extracting benchmarks. The MVP acquired beta customers and led to a seed raise round to attack the Phase II we helped form.',

    heroMetrics: [
        {
            label: 'Features shipped',
            value: '60'
        },
        {
            label: 'Weeks',
            value: '5'
        },
        {
            label: 'Team size',
            value: '2'
        }
    ],

    heroNarrative:
        'Straata\'s founder had the payment intelligence every processor wanted, but it lived in spreadsheets, decks, and billable hours. Larger firms scoped quarters of work and seven-figure budgets. We brought a two-person pod, wired Ether into every touchpoint, and attacked two fronts simultaneously: a public landing experience that told the Straata story, captured waitlist signups, and funneled investors into demos—and the product itself, a multi-tenant SaaS that ingests millions of transactions, calculates KPIs in real time, and productizes benchmarking. Five weeks later the consultancy was a product company raising on a v2 roadmap we left behind.',
    heroGalleryImage: heroOverview,
    heroGalleryPlaceholder: heroOverviewBlur,

    // ============================================
    // SCOPE OF WORK (4 Service Areas)
    // ============================================
    scopeOfWork: [
        {
            icon: 'lightbulb',
            title: 'Product Strategy',
            number: '01',
            description: 'Defined multi-tenant SaaS architecture, phased roadmap from MVP through Straata v2, and energy-based estimation that let leadership see exactly what was shipping at a glance inside Ether.'
        },
        {
            icon: 'design_services',
            title: 'UI/UX Design',
            number: '02',
            description: 'Designed analytics-first experience with account dashboards, benchmark widgets, saveable views, and guided explanations so non-experts can act on payment data instantly.'
        },
        {
            icon: 'api',
            title: 'API & Integration Development',
            number: '03',
            description: 'Built RESTful API framework with Swagger docs, Stripe Connect ingestion, spreadsheet imports, and background jobs engineered for millions of transactions with sub-2s query times.'
        },
        {
            icon: 'code',
            title: 'Accelerated Development',
            number: '04',
            description: 'Delivered 60 production-ready features across infrastructure, analytics, and transaction processing in five weeks with a two-person team—front to back from product to DevOps.'
        }
    ],

    // ============================================
    // CASE DETAILS (Collapsible Card)
    // ============================================
    caseDetails: {
        task: 'Transform Utopaya from a payments consultancy into a SaaS provider that packages expert analysis as instant, self-serve insights.',

        solution: 'Built Straata with multi-tenant architecture, server-side grids, Stripe Connect ingestion, spreadsheet uploads, and benchmark management—60 features spanning product, design, frontend, backend, database, and DevOps.',

        result: 'Straata shipped production-ready in five weeks. The team now onboards payment platforms instantly, serves benchmarks without slide decks, and is raising capital on a v2 roadmap we co-authored.',

        stack: [
            'React',
            'Next.js',
            'TypeScript',
            'Supabase',
            'PostgreSQL',
            'Stripe API',
            'AG Grid'
        ]
    },

    // ============================================
    // DYNAMIC CONTENT (Visual Storytelling)
    // ============================================
    content: [
        // LANDING GALLERY SECTION (BEFORE PROBLEM)
        {
            type: 'gallery',
            content: {
                heading: 'Landing Experience',
                caption: 'Investor-ready landing page that captured beta demand while the product took shape.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: landingImage,
                                alt: 'Straata landing page hero section',
                                caption: 'Landing hero—clean value prop for processors and investors'
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: landingFeatures,
                                alt: 'Straata landing features section',
                                caption: 'Feature + problem explanation with key visuals.'
                            },
                            {
                                src: landingCta,
                                alt: 'Straata beta signup form',
                                caption: 'Footer showing providers and CTA',
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: landingFull,
                                alt: "Login once users get an account",
                                caption: 'Clean login with OAuth.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        // PROBLEM TABLE
        {
            type: 'heading2',
            content: 'What Straata Solved',
            id: 'problem',
            description: 'Payment processors waste days on manual analysis'
        },
        {
            type: 'paragraph',
            content: 'Payment processors hire consultants to answer questions their own data should answer. Calculating effective rate means reconciling Stripe exports, merchant statements, and internal cost allocations—4-6 hours per merchant. Benchmarking pass-through rates requires tribal knowledge and slide decks. Straata turns payment expertise into instant, self-serve insights.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Before Straata', 'After Straata'],
                rows: [
                    ['"What\'s our effective rate this month?" requires 4-6 hours of Excel reconciliation across Stripe exports, merchant statements, and cost allocations', 'Effective Rate widget calculates instantly from transaction data with cost breakdowns and trend analysis'],
                    ['"How do we compare to industry benchmarks?" means hiring consultants or guessing based on outdated reports', 'Configurable benchmark ranges show exactly where you stand vs peers—by volume tier, card mix, and tender type'],
                    ['"Which merchants are underperforming on pass-through?" lives in someone\'s spreadsheet with manual filtering and pivot tables', 'Pass-Through Dashboard with RED/AMBER/GREEN status indicators surfaces margin opportunities automatically'],
                    ['Transaction reconciliation: export from Stripe, download merchant statements, match records manually, flag discrepancies in Excel', 'Stripe Connect auto-ingestion + server-side grid with 40-column filtering finds discrepancies in under 2 seconds'],
                    ['"What\'s our card mix doing to margins?" requires custom SQL queries and payment expertise to interpret', 'Card Mix donut chart (Visa 45%, MC 32%, Discover 8%) with average rates shows margin impact at a glance'],
                    ['Ops teams maintain separate views for each business unit using Excel filters and email attachments', 'Row-level security with saveable views—each BU sees only their data, leadership sees aggregated benchmarks']
                ]
            }
        },

        {
            type: 'divider'
        },

        // PLATFORM CAPABILITIES
        {
            type: 'heading2',
            content: 'Platform Capabilities',
            id: 'platform',
            description: '60 features across ingestion, dashboards, and reconciliation'
        },

        // SECTION 1: DASHBOARDING + PLATFORM ACCESS (1-2-1)
        {
            type: 'heading3',
            content: 'Dashboarding + Platform Access'
        },
        {
            type: 'paragraph',
            content: 'Multi-tenant SaaS foundation with OAuth authentication, account dashboards showing volume and pass-through metrics, and benchmark widgets that turn tribal knowledge into productized guidance.'
        },
        {
            type: 'gallery',
            content: {
                heading: '',
                caption: '',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: heroOverview,
                                alt: 'Straata dashboard fly-through',
                                caption: 'Primary Straata dashboard walkthrough—volume, pass-through, and benchmark widgets in motion',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                                placeholder: heroOverviewBlur
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: connectionCheckerImage,
                                alt: 'Connection health dashboard',
                                caption: 'Tenant admins confirm every processor, ACH rail, and legacy export is live before business units gain access.'
                            },
                            {
                                src: benchmarkConfigurationImage,
                                alt: 'Benchmark configuration matrix',
                                caption: 'Benchmark guardrails map business units to investor and operator groups so access stays scoped while aggregates stay accurate.'
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'mermaidGallery',
            content: {
                rows: [
                    {
                        layout: 'single',
                        diagrams: [
                            {
                                code: `flowchart TD
    Platform[(Straata Platform)]
    Platform --> TenantHub{{Companies / Tenants}}
    TenantHub --> Company1[Company 01]
    TenantHub --> Company2[Company 02]

    Company1 --> BU1[Business Unit 01]
    Company1 --> BU2[Business Unit 02]
    Company2 --> BU3[Business Unit 03]

    BU1 --> ConnStripe[Connection 01]
    BU1 --> ConnACH[Connection 02]
    BU2 --> ConnLegacy[Connection 03]
    BU3 --> ConnExports[Connection 04]

    subgraph UserAccess[User Groups / Access]
        OpsGroup[Ops Group]
        FinanceGroup[Finance Group]
        InvestorGroup[Investor Group]
    end

    OpsGroup --> BU1
    OpsGroup --> BU2
    FinanceGroup --> BU3

    ConnStripe --> DataLake[(Volume + Rate Warehouse)]
    ConnACH --> DataLake
    ConnLegacy --> DataLake
    ConnExports --> DataLake

    BU1 --> Weighted[Weighted Aggregates]
    BU2 --> Weighted
    BU3 --> Weighted
    DataLake --> Weighted
    Weighted --> InvestorGroup`,
                                title: 'Tenant / BU / Connection Matrix',
                                description: 'Hover to see how tenants, business units, connections, and user groups roll up into weighted investor views.'
                            }
                        ]
                    }
                ]
            }
        },
        // SECTION 2: TRANSACTION RECONCILIATION SUITE (1-1-2)
        {
            type: 'gallery',
            content: {
                heading: 'Transaction Reconciliation Suite',
                caption: 'Server-side AG Grid turns millions of payment rows into lightweight workflows with scoped views and saved benchmarks.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: merchantAggregatesVideo,
                                alt: 'Automatic merchant aggregates video',
                                caption: 'Automatic merchant aggregates once a connection is live—replacing a 12-hour manual reconciliation loop.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: transactionReconVideo,
                                alt: 'Server-side reconciliation workspace',
                                caption: 'Millions of records feel like one hundred thanks to server-side data modeling and grouped aggregates.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: savedViewsVideo,
                                alt: 'Saved reconciliation views',
                                caption: 'Saving views per business unit keeps groupings, filters, and isolation ready for the next recon session.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                src: savedMetricsVideo,
                                alt: 'Saved benchmark metrics',
                                caption: 'Teams bookmark benchmarks—including min / max / avg—so discoveries become reusable guardrails.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    }
                ]
            }
        },

        // SECTION 3: ADMIN + DELIVERY TOOLS (2-2-1)
        {
            type: 'heading3',
            content: 'Admin + Delivery Tools'
        },
        {
            type: 'paragraph',
            content: 'Stripe Connect integration for automatic transaction retrieval, spreadsheet upload with validation, and RESTful API with Swagger docs for third-party integrations.'
        },
        {
            type: 'gallery',
            content: {
                heading: '',
                caption: '',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: connectionCheckerImage,
                                alt: 'Straata connection manager showing scope status',
                                caption: 'Connection manager with key scope status, recon health, and backfill controls for every processor connection.'
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: benchmarkConfigurationImage,
                                alt: 'Straata benchmark CMS with living documentation',
                                caption: 'Benchmark CMS—living documentation that turns Straata’s benchmark catalog into editable guidance for every tenant.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        // TECHNICAL FEATS
        {
            type: 'heading2',
            content: 'Technical Feats',
            id: 'technical',
            description: 'Multi-tenant architecture and server-side grids from day one'
        },
        {
            type: 'paragraph',
            content: 'Straata had to handle millions of transaction records with ironclad tenant isolation. We engineered multi-tenant architecture and server-side grids from day one—no retrofitting, no performance surprises later.'
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'Multi-Tenant SaaS Architecture',
                description: 'Complete data isolation while enabling cross-platform analytics.',
                points: [
                    {
                        icon: 'shield',
                        title: 'Tenant-Scoped Isolation',
                        text: 'Row-level security with per-organization data boundaries ensures each client\'s payment data is completely isolated.'
                    },
                    {
                        icon: 'speed',
                        title: 'Real-Time Processing',
                        text: 'Transaction ingestion and metric calculation happen in near real-time—upload data, see KPIs update instantly.'
                    },
                    {
                        icon: 'api',
                        title: 'API-First Design',
                        text: 'RESTful endpoints with Swagger documentation provide clear interfaces for third-party integrations.'
                    }
                ]
            }
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'Server-Side Data Grid for Scale',
                description: 'Handling millions of rows with 40+ columns per payment platform.',
                points: [
                    {
                        icon: 'grid_on',
                        title: 'AG Grid Enterprise',
                        text: 'Server-side rendering handles massive datasets without browser memory limits—millions of rows respond in under two seconds.'
                    },
                    {
                        icon: 'analytics',
                        title: 'Dynamic Aggregation',
                        text: 'Group and roll up data across any column combination for flexible analysis without pre-computing every permutation.'
                    },
                    {
                        icon: 'bolt',
                        title: 'Lazy Loading',
                        text: 'Virtualization and pagination provide instant response times regardless of dataset size.'
                    }
                ]
            }
        },


    ]
};

export default straataMvp;
